// ABOUTME: Zod schemas and inferred types for TechLog sector API data and sector write inputs.
import { Zdate, ZdateTime } from '../common.zod';
import { ZTechLogAttachment } from './attachment.zod';
import { TechlogFlightStatus } from './sector';
import { z } from 'zod';

// Simple types
const ZgpuTime = z
  .string()
  .describe('GPU (Ground Power Unit) time in HH:MM:SS format');

// For any types where the implementation isn't fully specified, we'll create a reasonable schema
const ZpreflightCustomData = z
  .record(z.unknown())
  .describe('Custom data recorded during preflight checks');
const ZpostflightCustomData = z
  .record(z.unknown())
  .describe('Custom data recorded during postflight checks');
const Zcycles = z
  .object({
    airframe: z
      .number()
      .nullable()
      .optional()
      .describe('Total number of airframe cycles'),
    engine_1: z
      .number()
      .nullable()
      .optional()
      .describe('Total number of cycles for engine 1'),
    engine_2: z
      .number()
      .nullable()
      .optional()
      .describe('Total number of cycles for engine 2'),
    engine_3: z
      .number()
      .nullable()
      .optional()
      .describe('Total number of cycles for engine 3'),
    engine_4: z
      .number()
      .nullable()
      .optional()
      .describe('Total number of cycles for engine 4'),
    apu: z
      .number()
      .nullable()
      .optional()
      .describe('Total number of APU cycles'),
    pressure: z
      .number()
      .nullable()
      .optional()
      .describe('Total number of pressurization cycles'),
  })
  .passthrough()
  .describe('Aircraft component cycle counts');

// Enum
export const ZTechlogFlightStatus = z
  .nativeEnum(TechlogFlightStatus)
  .describe('Status of the flight in TechLog');

// Main TechLogSector schema - reflects actual TechLog API response with nullable fields
export const ZTechLogSector = z.object({
  id: z.string().describe('Unique identifier for the sector'),
  tripId: z.string().describe('ID of the trip this sector belongs to'),
  preflightSignatureId: z
    .string()
    .nullable()
    .optional()
    .describe('ID of the electronic signature for preflight checks'),
  postflightSignatureId: z
    .string()
    .nullable()
    .optional()
    .describe('ID of the electronic signature for postflight checks'),
  departureAirportId: z.number().describe('ID of the departure airport'),
  arrivalAirportId: z
    .number()
    .nullable()
    .optional()
    .describe('ID of the planned arrival airport when available'),
  actualArrivalAirportId: z
    .number()
    .nullable()
    .optional()
    .describe('ID of the actual arrival airport (may differ from planned)'),
  pilotInCommandId: z
    .string()
    .nullable()
    .optional()
    .describe('ID of the Pilot in Command (PIC)'),
  secondInCommandId: z
    .string()
    .nullable()
    .optional()
    .describe('ID of the Second in Command (SIC) or First Officer'),
  pilotPerformingTakeoffId: z
    .string()
    .nullable()
    .optional()
    .describe('ID of the pilot who performed the takeoff'),
  pilotPerformingLandingId: z
    .string()
    .nullable()
    .optional()
    .describe('ID of the pilot who performed the landing'),
  userId: z
    .string()
    .nullable()
    .optional()
    .describe('ID of the user who created/owns this sector record'),
  apuSeconds: z
    .number()
    .nullable()
    .optional()
    .describe('Total seconds the APU (Auxiliary Power Unit) was running'),
  apuReading: z
    .number()
    .nullable()
    .optional()
    .describe(
      'APU (Auxiliary Power Unit) hour meter reading, held in seconds: divide by 3600 for the hours the meter shows',
    ),
  apuCyclesReading: z
    .number()
    .nullable()
    .optional()
    .describe('APU (Auxiliary Power Unit) cycles counter reading'),
  hobbsDeparture: z
    .number()
    .nullable()
    .optional()
    .describe(
      'Hobbs meter reading at departure (aircraft operating time in hours)',
    ),
  hobbsArrival: z
    .number()
    .nullable()
    .optional()
    .describe(
      'Hobbs meter reading at arrival (aircraft operating time in hours)',
    ),
  offblocksTime: ZdateTime.nullable()
    .optional()
    .describe('Time when aircraft moved off blocks (start of taxi)'),
  takeoffTime: ZdateTime.nullable().optional().describe('Time of takeoff'),
  landingTime: ZdateTime.nullable().optional().describe('Time of landing'),
  onblocksTime: ZdateTime.nullable()
    .optional()
    .describe('Time when aircraft reached blocks (end of taxi)'),
  landingsCount: z
    .number()
    .nullable()
    .optional()
    .describe('Number of landings performed during this sector'),
  takeoffsCount: z
    .number()
    .nullable()
    .optional()
    .describe('Number of takeoffs performed during this sector'),
  status: ZTechlogFlightStatus.describe(
    'Current status of the flight (complete or planned)',
  ),
  flightType: z
    .string()
    .nullable()
    .optional()
    .describe('Type of flight (e.g., training, commercial, ferry)'),
  remarks: z
    .string()
    .nullable()
    .optional()
    .describe('Additional notes or comments about the sector'),
  createdAt: ZdateTime.describe('Timestamp when this record was created'),
  updatedAt: ZdateTime.describe('Timestamp when this record was last updated'),
  date: Zdate.describe('Calendar date of the flight'),
  source: z
    .string()
    .nullable()
    .optional()
    .describe('Source system that created this record (e.g., techlogApp)'),

  preflightCustomData: ZpreflightCustomData.optional().describe(
    'Custom data recorded during preflight checks',
  ),
  postflightCustomData: ZpostflightCustomData.optional().describe(
    'Custom data recorded during postflight checks',
  ),
  cycles: Zcycles.optional().describe(
    'Count of cycles for various aircraft components',
  ),
  passengersMale: z
    .number()
    .nullable()
    .optional()
    .describe('Number of adult male passengers'),
  passengersFemale: z
    .number()
    .nullable()
    .optional()
    .describe('Number of adult female passengers'),
  passengersChildren: z
    .number()
    .nullable()
    .optional()
    .describe('Number of child passengers (typically ages 2-12)'),
  passengersInfants: z
    .number()
    .nullable()
    .optional()
    .describe('Number of infant passengers (typically under 2 years)'),
  baggageWeight: z
    .number()
    .nullable()
    .optional()
    .describe('Total weight of baggage in standard units'),
  gpuStart: ZgpuTime.nullable()
    .optional()
    .describe('Time when GPU (Ground Power Unit) was connected'),
  gpuEnd: ZgpuTime.nullable()
    .optional()
    .describe('Time when GPU (Ground Power Unit) was disconnected'),
  gpuSeconds: z
    .number()
    .nullable()
    .optional()
    .describe('Total seconds the GPU (Ground Power Unit) was connected'),
  maxFlightLevel: z
    .number()
    .nullable()
    .optional()
    .describe(
      'Maximum flight level reached during the flight (in hundreds of feet)',
    ),
  pressurisedCycle: z
    .boolean()
    .nullable()
    .optional()
    .describe('Whether the aircraft went through a pressurization cycle'),
  preflightMxCheckIds: z
    .array(z.string())
    .optional()
    .describe('IDs of maintenance checks performed before the flight'),
  abandonmentReason: z
    .string()
    .nullable()
    .optional()
    .describe(
      'Reason for flight abandonment if applicable (e.g., technicalFailure)',
    ),
  attachments: z
    .array(ZTechLogAttachment)
    .optional()
    .describe('Documents or files attached to this sector record'),
  fratScore: z
    .number()
    .nullable()
    .optional()
    .describe('Flight Risk Assessment Tool score'),
  plannedFuelBurn: z
    .number()
    .nullable()
    .optional()
    .describe('Planned fuel consumption for the flight in kg'),
  actualFuelBurn: z
    .number()
    .nullable()
    .optional()
    .describe('Actual fuel consumed during the flight in kg'),
  fuelBurnJustification: z
    .string()
    .nullable()
    .optional()
    .describe(
      'Explanation for any discrepancy between planned and actual fuel burn',
    ),
});

// TimeData schema - define it inline since it's not directly exported from sector.ts
export const ZTimeData = z.object({
  Type: z.string().describe('Type of time data (e.g., departure, arrival)'),
  DateTime: Zdate.describe('Date and time of the event'),
});

// NavblueFlightDataInput schema
export const ZNavblueFlightDataInput = z.object({
  FlightNumber: z
    .string()
    .optional()
    .describe('Flight number assigned by the airline'),
  UniqueId: z
    .string()
    .optional()
    .describe('Unique identifier for the flight in the Navblue system'),
  Times: z
    .object({
      Time: z.array(ZTimeData).describe('Array of time events for the flight'),
    })
    .optional()
    .describe('Collection of time-related data points for the flight'),
  Airports: z
    .object({
      DepartureAirport: z
        .object({
          ICAO: z
            .string()
            .describe(
              'ICAO (International Civil Aviation Organization) code for departure airport -- 4 letter code',
            ),
        })
        .describe('Information about the departure airport'),
      ArrivalAirport: z
        .object({
          ICAO: z
            .string()
            .describe(
              'ICAO (International Civil Aviation Organization) code for arrival airport -- 4 letter code',
            ),
        })
        .describe('Information about the arrival airport'),
    })
    .optional()
    .describe('Information about departure and arrival airports'),
  VoyageReport: z
    .object({
      ArrivalPilot: z.string().describe('Name or ID of the pilot at arrival'),
    })
    .optional()
    .describe('Report about the voyage details'),
});

// TechlogSectorCreateInput schema
export const ZTechlogSectorCreateInput = z.object({
  tripId: z.string().describe('ID of the trip this sector belongs to'),
  departureAirportId: z.number().describe('ID of the departure airport'),
  arrivalAirportId: z.number().describe('ID of the planned arrival airport'),
  actualArrivalAirportId: z
    .number()
    .optional()
    .describe('ID of the actual arrival airport (may differ from planned)'),
  pilotInCommandId: z
    .string()
    .optional()
    .describe('ID of the Pilot in Command (PIC)'),
  secondInCommandId: z
    .string()
    .optional()
    .describe('ID of the Second in Command (SIC) or First Officer'),
  userId: z
    .string()
    .optional()
    .describe('ID of the user who created/owns this sector record'),
  apuSeconds: z
    .number()
    .optional()
    .describe('Total seconds the APU (Auxiliary Power Unit) was running'),
  apuReading: z
    .number()
    .optional()
    .describe(
      'APU (Auxiliary Power Unit) hour meter reading, held in seconds: divide by 3600 for the hours the meter shows',
    ),
  apuCyclesReading: z
    .number()
    .optional()
    .describe('APU (Auxiliary Power Unit) cycles counter reading'),
  hobbsDeparture: z
    .number()
    .optional()
    .describe(
      'Hobbs meter reading at departure (aircraft operating time in hours)',
    ),
  hobbsArrival: z
    .number()
    .optional()
    .describe(
      'Hobbs meter reading at arrival (aircraft operating time in hours)',
    ),
  offblocksTime: ZdateTime.optional().describe(
    'Time when aircraft moved off blocks (start of taxi)',
  ),
  takeoffTime: ZdateTime.describe('Time of takeoff'),
  landingTime: ZdateTime.describe('Time of landing'),
  onblocksTime: ZdateTime.optional().describe(
    'Time when aircraft reached blocks (end of taxi)',
  ),
  landingsCount: z
    .number()
    .optional()
    .describe('Number of landings performed during this sector'),
  preflightCustomData: ZpreflightCustomData.optional().describe(
    'Custom data recorded during preflight checks',
  ),
  flightType: z
    .string()
    .optional()
    .describe('Type of flight (e.g., training, commercial, ferry)'),
  flightId: z
    .string()
    .optional()
    .describe('ID of the flight in an external system'),
  fratScore: z
    .number()
    .optional()
    .describe('Flight Risk Assessment Tool score'),
  plannedFuelBurn: z
    .number()
    .optional()
    .describe('Planned fuel consumption for the flight in kg'),
  actualFuelBurn: z
    .number()
    .optional()
    .describe('Actual fuel consumed during the flight in kg'),
  fuelBurnJustification: z
    .string()
    .optional()
    .describe(
      'Explanation for any discrepancy between planned and actual fuel burn',
    ),
});

// TechlogSyncSectorInput schema
export const ZTechlogSyncSectorInput = ZTechlogSectorCreateInput.extend({
  id: z.string().optional().describe('Unique identifier for the sector'),
  originId: z
    .string()
    .optional()
    .describe('Original ID of the sector in the source system'),
  leonFlightId: z
    .number()
    .optional()
    .describe('ID of the flight in the Leon system'),
});

/**
 * Sector schema used at the sync-flow boundary. Extends the canonical
 * `ZTechlogSyncSectorInput` with workflow-attached carry fields. See
 * `TechlogSyncSectorInputWithSyncMetadata` in `sector.ts` for the full
 * rationale. None of these fields are sent to the TechLog API.
 */
export const ZTechlogSyncSectorInputWithSyncMetadata =
  ZTechlogSyncSectorInput.extend({
    aircraft: z
      .string()
      .optional()
      .describe(
        'Source-system aircraft for this specific flight (per-sector). Used by the cross-aircraft pre-check in syncTechlogTripsAndSectors.',
      ),
    syncedDocId: z
      .string()
      .optional()
      .describe(
        'Firestore sync record id this sector was previously synced under. Workflow-attached carry field.',
      ),
    syncedRegistration: z
      .string()
      .optional()
      .describe(
        'Firestore subcollection (registration) this sector was previously synced under. Workflow-attached carry field.',
      ),
  });

// Export type assertions for TypeScript integration
export type ZTechLogSectorType = z.infer<typeof ZTechLogSector>;
export type ZTechlogSectorCreateInputType = z.infer<
  typeof ZTechlogSectorCreateInput
>;
export type ZTechlogSyncSectorInputType = z.infer<
  typeof ZTechlogSyncSectorInput
>;
export type ZTechlogSyncSectorInputWithSyncMetadataType = z.infer<
  typeof ZTechlogSyncSectorInputWithSyncMetadata
>;
export type ZNavblueFlightDataInputType = z.infer<
  typeof ZNavblueFlightDataInput
>;
